import React, { useState } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Card, CardContent } from "./ui/card";
import {
  CheckCircle,
  ArrowRight,
  Users,
  Building2,
  Trophy,
  Clock,
  AlertCircle,
} from "lucide-react";
import { mockData } from "../mock";

export const Hero = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
  });

  const [submitted, setSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Input handler
  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (error) setError(null);
  };

  // ✅ SIMPLE SUBMIT → WHATSAPP
  const handleSubmit = (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    const { name, email, phone } = formData;

    if (!name || !email || !phone) {
      setError("Please fill all fields");
      setIsLoading(false);
      return;
    }

    const message = `
SkillBay Enquiry

Name: ${name}
Email: ${email}
Phone: ${phone}
    `;

    const whatsappNumber = "919895223307"; 
    const encodedMessage = encodeURIComponent(message);
    const whatsappURL = `https://wa.me/${whatsappNumber}?text=${encodedMessage}`;

    // Open WhatsApp
    window.open(whatsappURL, "_blank");

    // UI success state
    setSubmitted(true);
    setFormData({ name: "", email: "", phone: "" });

    setTimeout(() => setSubmitted(false), 5000);

    setIsLoading(false);
  };

  return (
    <section id="hero-container" className="relative h-[90vh] flex items-center bg-gradient-to-br from-slate-50 via-white to-gray-50">
      <div className="absolute inset-0 bg-grid-pattern opacity-5 pointer-events-none"></div>

      <div id="hero" className="relative container mx-auto px-4 py-20">
        <div className="grid lg:grid-cols-2 gap-12 items-center">

          {/* LEFT CONTENT */}
          <div className="space-y-8">
            <div className="space-y-5">
              <h1 className="text-4xl lg:text-6xl font-bold text-gray-900 leading-tight">
                Learn with Purpose.
                <span className="text-slate-600 relative block">
                  Engineer with Precision.
                  <div className="absolute -bottom-2 left-0 w-full h-3 bg-slate-200 -skew-y-1 -z-10"></div>
                </span>
              </h1>

              <p className="text-xl text-gray-700 leading-relaxed">
                <strong>GJ SkillBay</strong> is an industry-driven learning
                platform with 4+ years of software excellence at Govt.
                Cyberpark, Kozhikode.
              </p>
            </div>

            {/* STATS */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="text-center">
                <div className="flex items-center justify-center gap-2 text-3xl font-bold">
                  <Users size={26} className="text-gray-500" />
                  <span className="text-gray-500">300+</span>
                </div>
                <p className="text-sm text-gray-500">Students</p>
              </div>

              <div className="text-center">
                <div className="flex items-center justify-center gap-2 text-3xl font-bold">
                  <Building2 size={26} className="text-gray-500" />
                  <span className="text-gray-500">50+</span>
                </div>
                <p className="text-sm text-gray-500">Projects</p>
              </div>

              <div className="text-center">
                <div className="flex items-center justify-center gap-2 text-3xl font-bold text-slate-600">
                  <Trophy size={26} />4+
                </div>
                <p className="text-sm text-gray-500">Years GJ Global</p>
              </div>

              <div className="text-center">
                <div className="flex items-center justify-center gap-2 text-3xl font-bold text-slate-600">
                  <Clock size={26} />40+
                </div>
                <p className="text-sm text-gray-500">Expert Mentors</p>
              </div>
            </div>

            {/* CTA BUTTONS */}
            <div className="hidden lg:flex items-center gap-4">
              <Button
                size="lg"
                className="bg-slate-600 hover:bg-slate-700 text-white px-8 py-6 text-lg"
                onClick={() =>
                  window.open(
                    "https://api.whatsapp.com/send?phone=919895223307",
                    "_blank"
                  )
                }
              >
                Get Syllabus
                <ArrowRight className="ml-2" size={20} />
              </Button>

              <Button
                variant="outline"
                size="lg"
                className="px-8 py-6 text-lg border-2 hover:bg-gray-50"
                onClick={() =>
                  window.open(`tel:${mockData.contact.phone}`, "_self")
                }
              >
                Talk to an advisor
              </Button>
            </div>
          </div>

          {/* RIGHT CONTENT – FORM */}
          <div className="relative">
            <Card
              className="bg-white/80 backdrop-blur-sm shadow-2xl border-0"
              id="hero-form"
            >
              <CardContent className="p-8">
                <div className="space-y-6">

                  <div className="text-center space-y-2">
                    <h3 className="text-2xl font-bold text-gray-900">
                      Get Syllabus
                    </h3>
                  </div>

                  {!submitted ? (
                    <form onSubmit={handleSubmit} className="space-y-4">

                      {error && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2 text-red-700">
                          <AlertCircle size={16} />
                          <span className="text-sm">{error}</span>
                        </div>
                      )}

                      <div>
                        <Label htmlFor="name">Full Name</Label>
                        <Input
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          required
                          className="mt-1"
                          disabled={isLoading}
                          placeholder="Enter your name"
                        />
                      </div>

                      <div>
                        <Label htmlFor="email">Email Address</Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          required
                          className="mt-1"
                          disabled={isLoading}
                          placeholder="your email@gmail.com"
                        />
                      </div>

                      <div>
                        <Label htmlFor="phone">WhatsApp Number</Label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={handleInputChange}
                          required
                          className="mt-1"
                          disabled={isLoading}
                          placeholder="+91 9877656551"
                        />
                      </div>

                      <Button
                        type="submit"
                        size="lg"
                        className="w-full bg-slate-600 hover:bg-slate-700 text-white py-6 text-lg disabled:opacity-50"
                        disabled={isLoading}
                      >
                        {isLoading ? (
                          <>
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                            Submitting...
                          </>
                        ) : (
                          <>
                            Submit
                            <ArrowRight className="ml-2" size={20} />
                          </>
                        )}
                      </Button>
                    </form>
                  ) : (
                    <div className="text-center space-y-4 py-8">
                      <CheckCircle
                        className="mx-auto text-green-500"
                        size={48}
                      />
                      <h4 className="text-xl font-semibold text-green-700">
                        Success!
                      </h4>
                      <p className="text-gray-600">
                        WhatsApp opened with your details.
                      </p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* MOBILE CTA */}
            <div className="lg:hidden mt-6 flex flex-col gap-3">
              <Button
                size="lg"
                className="bg-slate-600 hover:bg-slate-700 text-white py-6 text-lg w-full"
                onClick={() =>
                  document
                    .getElementById("hero-form")
                    ?.scrollIntoView({ behavior: "smooth" })
                }
              >
                Get Syllabus
                <ArrowRight className="ml-2" size={20} />
              </Button>

              <Button
                variant="outline"
                size="lg"
                className="py-6 text-lg border-2 hover:bg-gray-50 w-full"
                onClick={() =>
                  document
                    .getElementById("mentors")
                    ?.scrollIntoView({ behavior: "smooth" })
                }
              >
                Book Free Mentor Call
              </Button>
            </div>

          </div>
        </div>
      </div>
    </section>
  );
};
